import { FC, useMemo, useState } from 'react';
import { TrashIcon } from '@heroicons/react/outline';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';

import {
  MailSequenceEnrollmentStatus,
  MailSequenceScheduledSendStatus,
  useMailSequenceCancelEnrollmentMutation,
  useMailSequenceEnrollmentQuery,
} from '../../../../generated/graphql';

import ActionButton from '../../../components/ActionButton';
import Button from '../../../components/Button';
import Modal from '../../../components/Modal';
import { customToast } from '../../../components/ToastAlert/customToast';
import { getShortDate } from '../../../lib/time';
import { pluralize } from '../../../lib/copy';
import {
  computeOffsetDaysFromDates,
  referenceDateLabel,
} from '../../../lib/mailSequence';
import { buildScheduledSendCaption } from '../../ScheduleMail/anchorDates';
import Spinner from '../../../svgs/Spinner';
import SequenceSummary from './SequenceSummary';
import ScheduledSendCard from './ScheduledSendCard';
import SkipMailConfirmModal from './SkipMailConfirmModal';

interface OutletContext {
  refetchEnrollments: () => void;
}

const EnrollmentDetails: FC = () => {
  const { enrollmentId } = useParams();
  const navigate = useNavigate();
  const { refetchEnrollments } = useOutletContext<OutletContext>();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [skipPendingId, setSkipPendingId] = useState<string | null>(null);

  const { data, loading, refetch } = useMailSequenceEnrollmentQuery({
    variables: { enrollmentId: enrollmentId! },
    skip: !enrollmentId,
    fetchPolicy: 'cache-and-network',
    onError: (err) =>
      customToast.error(err.message || 'Could not load mail sequence.'),
  });

  const enrollment = data?.mailSequenceEnrollment;

  const [cancelMutation, { loading: cancelling }] =
    useMailSequenceCancelEnrollmentMutation({
      onCompleted: ({ mailSequenceCancelEnrollment }) => {
        if (mailSequenceCancelEnrollment?.success) {
          customToast.success('Mail sequence cancelled.');
          setConfirmCancel(false);
          refetch();
          refetchEnrollments();
        } else {
          customToast.error(
            mailSequenceCancelEnrollment?.error ||
              'Could not cancel mail sequence.',
          );
        }
      },
      onError: (err) =>
        customToast.error(err.message || 'Could not cancel mail sequence.'),
    });

  const sortedSends = useMemo(
    () =>
      enrollment
        ? [...enrollment.scheduledSends].sort(
            (a, b) => a.stepIndex - b.stepIndex,
          )
        : [],
    [enrollment],
  );

  const skippedCount = sortedSends.filter(
    (s) => s.status === MailSequenceScheduledSendStatus.Skipped,
  ).length;

  const anchorLabel = enrollment
    ? referenceDateLabel(enrollment.referenceDateType).toLowerCase()
    : '';
  const referenceDateIso = enrollment?.referenceDate.slice(0, 10) ?? '';

  const isActive = enrollment?.status === MailSequenceEnrollmentStatus.Active;

  if (loading && !enrollment) {
    return <Spinner className="mx-auto mt-8" />;
  }

  if (!enrollment) return null;

  const openMailInTab = (send: typeof sortedSends[number]) => {
    const programId = enrollment.programId;
    if (send.programFollowUpId) {
      navigate(`/clients/${programId}/follow-ups/${send.programFollowUpId}`);
      return;
    }
    navigate(`/clients/${programId}/follow-ups/scheduled/${send.id}`);
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto px-8 py-6">
        <SequenceSummary
          enrollment={enrollment}
          onNameUpdated={() => {
            refetch();
            refetchEnrollments();
          }}
          headerActions={
            isActive ? (
              <ActionButton
                size="small"
                aria-label="Cancel Mail sequence"
                title="Cancel Mail sequence"
                onClick={() => setConfirmCancel(true)}
              >
                <TrashIcon className="h-4 w-4 text-red-100" />
              </ActionButton>
            ) : undefined
          }
        />

        {skippedCount > 0 && (
          <div className="bg-yellow-25/60 mt-4 flex items-start gap-2 rounded-md px-3 py-2">
            <span aria-hidden className="text-yellow-150 mt-0.5">
              ⚠
            </span>
            <p className="text-small-caption text-neutral-150">
              {pluralize(skippedCount, 'Mail was', 'Mails were')} skipped on
              this Mail sequence.
            </p>
          </div>
        )}

        <div className="mt-6">
          {/* Extra padding keeps card shadows from being clipped by overflow-x. */}
          <div className="-mx-1 flex snap-x snap-mandatory items-stretch gap-6 overflow-x-auto overscroll-x-contain px-3 pb-5 pt-3">
            {sortedSends.map((send) => {
              const skippable =
                isActive &&
                (send.status === MailSequenceScheduledSendStatus.Pending ||
                  send.status === MailSequenceScheduledSendStatus.Failed);
              const { suffix, secondary } = buildScheduledSendCaption({
                status: send.status,
                scheduledDateLabel: getShortDate(send.scheduledDate),
                sentAtLabel: send.sentAt ? getShortDate(send.sentAt) : null,
                offsetDays: computeOffsetDaysFromDates(
                  enrollment.referenceDate,
                  send.scheduledDate,
                ),
                anchorLabel,
                anchorDateIso: referenceDateIso,
              });

              return (
                <div key={send.id} className="flex shrink-0 self-stretch">
                  <ScheduledSendCard
                    send={{
                      stepIndex: send.stepIndex,
                      subject: send.subject,
                      providerNote: send.providerNote,
                      status: send.status,
                      skipReason: send.skipReason,
                      failureReason: send.failureReason,
                      activityCount: send.activities.length,
                    }}
                    captionSuffix={suffix}
                    captionSecondary={secondary}
                    canSkip={skippable}
                    onSkip={() => setSkipPendingId(send.id)}
                    onClick={() => openMailInTab(send)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <Modal
        isOpen={confirmCancel}
        setClosed={() => setConfirmCancel(false)}
        width="small"
      >
        <div className="px-10 py-8">
          <div className="flex flex-col items-center">
            <div className="mb-2 flex text-center font-serif text-subtitle-small text-green-150">
              Cancel this Mail sequence?
            </div>
            <div className="text-center text-body text-green-125">
              Mail already sent in this sequence won&apos;t be affected. All
              upcoming Mail will be cancelled and no further messages will be
              delivered.
            </div>
          </div>
          <div className="mt-11 flex w-full flex-row items-center justify-center">
            <Button
              title="Keep Mail sequence"
              theme="secondary"
              className="mr-5"
              onClick={() => setConfirmCancel(false)}
              disabled={cancelling}
            />
            <Button
              title={cancelling ? 'Cancelling...' : 'Cancel Mail sequence'}
              theme="primary"
              disabled={cancelling}
              onClick={() =>
                cancelMutation({
                  variables: { enrollmentId: enrollment.id },
                })
              }
            />
          </div>
        </div>
      </Modal>

      <SkipMailConfirmModal
        isOpen={Boolean(skipPendingId)}
        scheduledSendId={skipPendingId}
        onClose={() => setSkipPendingId(null)}
        onSkipped={() => {
          refetch();
          refetchEnrollments();
        }}
      />
    </div>
  );
};

export default EnrollmentDetails;
